import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import Page from '../components/Page';
import Pill from '../components/Pill';
import ContractsStrip from '../components/ContractsStrip';
import LiveBlockText from '../components/LiveBlockText';
import { fmt, weiToEth, short } from '../lib/format';
import { useAccount } from '../hooks/useWallet';
import { useReadContracts } from '../hooks/useContracts';
import { useCountdown, formatCountdown } from '../hooks/useCountdown';
import {
  useCurrentPeriod,
  usePeriodRaisedWei,
  useRemainingPeriodTime,
  useVaultBalance,
  useContributorsLength,
  useAccountClaimableTokens,
  useTokensCreatedPerPeriod,
  useTotalWeiRaised,
} from '../hooks/useCrowdsale';
import { useTotalSupply, useBalanceOf } from '../hooks/useToken';
import { useRequestCount, useTotalClaimedByLabs } from '../hooks/useAuditingPoolConfig';
import { type RequestRow } from '../hooks/useRequests';
import { useProposalCount, type ProposalRow } from '../hooks/useGovernance';

export default function Overview() {
  const account = useAccount();
  const contracts = useReadContracts();

  const period           = useCurrentPeriod();
  const raised           = usePeriodRaisedWei(0);
  const ttnp             = useRemainingPeriodTime();
  const vault            = useVaultBalance();
  const supply           = useTotalSupply();
  const contributors     = useContributorsLength(0);
  const balance          = useBalanceOf(account);
  const claimable        = useAccountClaimableTokens(account);
  const tokensPerPeriod  = useTokensCreatedPerPeriod();
  const totalRaised      = useTotalWeiRaised();
  const totalClaimed     = useTotalClaimedByLabs();
  const requestCount     = useRequestCount();
  const proposalCount    = useProposalCount();

  // Note: bigint 0n is falsy, so use explicit `!== undefined` to keep zero values.
  const periodNum     = period.data !== undefined ? Number(period.data) : null;
  const raisedEth     = raised.data !== undefined ? weiToEth(raised.data) : null;
  const supplyMeth    = supply.data !== undefined ? weiToEth(supply.data) : null;
  const vaultEth      = vault.data !== undefined ? weiToEth(vault.data) : null;
  const balanceMeth   = balance.data !== undefined ? weiToEth(balance.data) : 0;
  const claimableMeth = claimable.data !== undefined ? weiToEth(claimable.data) : 0;
  const totalRaisedEth = totalRaised.data !== undefined ? weiToEth(totalRaised.data) : null;
  const fundedToLabsEth = totalClaimed.data !== undefined ? weiToEth(totalClaimed.data) : null;

  // vote weight share — ((balance / supply) * 100)
  const voteWeightPct =
    balance.data && supply.data && supply.data > 0n
      ? (Number(balance.data) / Number(supply.data)) * 100
      : null;

  // Live sparkline: ETH raised across the last 100 closed periods (user
  // request). Aggressive staleTime + staggered cache so we don't re-fire
  // 100 RPC reads on every render.
  const lastClosed = Math.max(0, (periodNum ?? 0) - 1);
  const sparkN = Math.min(100, lastClosed);
  const sparkStart = Math.max(1, lastClosed - sparkN + 1);
  const sparkPids = Array.from({ length: sparkN }, (_, i) => sparkStart + i);
  const sparkQueries = useQueries({
    queries: sparkPids.map((pid) => ({
      queryKey: ['metha', 'crowdsale', 'getWeiRaisedPerPeriod', pid] as const,
      queryFn: async () => {
        const wei = (await contracts.crowdsale.getWeiRaisedPerPeriod(BigInt(pid))) as bigint;
        return weiToEth(wei);
      },
      // Closed periods don't change once settled, so cache aggressively.
      staleTime: 5 * 60_000,
    })),
  });
  // Per-period values, append the live period as the trailing point.
  const perPeriod = sparkQueries.map((q) => q.data ?? 0);
  if (raisedEth !== null) perPeriod.push(raisedEth);
  // Cumulative running sum — this is what the user asked for: total ETH
  // raised over time. Visually it never decreases.
  const sparkValues: number[] = [];
  let acc = 0;
  for (const v of perPeriod) {
    acc += v;
    sparkValues.push(acc);
  }

  // Live request list — fan out across the requests, then sort by hype DESC
  // and slice the top 6 that are still open (not cancelled, no verdict yet).
  const reqN = requestCount.data !== undefined ? Number(requestCount.data) : 0;
  const requestQueries = useQueries({
    queries: Array.from({ length: reqN }, (_, i) => ({
      queryKey: ['metha', 'auditingPool', 'requestList', i] as const,
      queryFn: async () => (await contracts.auditingPool.requestList(BigInt(i))) as unknown as RequestRow,
      staleTime: 30_000,
    })),
  });
  const topRequests = requestQueries
    .map((q, i) => (q.data ? { id: i, row: q.data } : null))
    .filter((x): x is { id: number; row: RequestRow } => x !== null)
    // Open = no verdict cast yet AND not cancelled. Sorted by hype DESC.
    .filter(({ row }) => !row.cancelled && !row.verdictCast)
    .sort((a, b) => (b.row.hype > a.row.hype ? 1 : b.row.hype < a.row.hype ? -1 : 0))
    .slice(0, 6);

  // Live proposal list — only the ones with active deadlines.
  const propN = proposalCount.data !== undefined ? Number(proposalCount.data) : 0;
  const proposalQueries = useQueries({
    queries: Array.from({ length: propN }, (_, i) => ({
      queryKey: ['metha', 'governance', 'proposalsMapping', i] as const,
      queryFn: async () => (await contracts.governance.proposalsMapping(BigInt(i))) as unknown as ProposalRow,
      staleTime: 20_000,
    })),
  });
  const now = Math.floor(Date.now() / 1000);
  const openProposals = proposalQueries
    .map((q, i) => (q.data ? { id: i, row: q.data } : null))
    .filter((x): x is { id: number; row: ProposalRow } => x !== null)
    .filter(({ row }) => Number(row.deadLine) > now && !row.isSuccesfull)
    .slice(0, 4);

  return (
    <Page ctxLabel="dapp" title="Metha · Overview" docsPath="overview" docsLabel="Protocol overview">

      <div className="ph">
        <div>
          <h1>
            Overview <span className="acc">/ period {periodNum ?? '…'}</span>
          </h1>
          <p className="lede">
            Live reading of the Metha protocol on Sepolia. Every number below is
            read from a verified contract — no off-chain dashboards, no chart smoothing.
          </p>
        </div>
        <div className="meta">
          <span>BLOCK <LiveBlockText /></span>
          <span>NEXT PERIOD <b>{ttnp.data !== undefined ? fmt.ttlShort(Number(ttnp.data)) : '—'}</b></span>
          <span>NETWORK <b>SEPOLIA</b></span>
        </div>
      </div>

      <div className="row r-2u1 mb-14">
        <div className="panel">
          <div className="p-head">
            <h3>ETH raised · current & lifetime</h3>
            <span className="src"><code>getWeiRaisedPerPeriod(0) · getWeiRaised()</code></span>
          </div>
          <div className="big-stat">
            <div style={{ display: 'flex', gap: 32, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <div className="l">CURRENT PERIOD</div>
                <div className="v">
                  {raisedEth != null ? raisedEth.toFixed(4) : '— '}
                  <span className="u">ETH</span>
                </div>
              </div>
              <div>
                <div className="l">TOTAL RAISED</div>
                <div className="v" style={{ color: 'var(--ctx)' }}>
                  {totalRaisedEth != null ? totalRaisedEth.toFixed(4) : '— '}
                  <span className="u">ETH</span>
                </div>
              </div>
            </div>
            <div className="d">
              {contributors.data != null
                ? `${Number(contributors.data)} contributors live`
                : 'fetching live aggregator…'}
            </div>
            <div style={{ display: 'flex', gap: 14, alignItems: 'stretch', marginTop: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Sparkline values={sparkValues} startPeriod={sparkStart} />
                <div className="mono dim" style={{ fontSize: 9, letterSpacing: 0.8, marginTop: 4, textAlign: 'right' }}>
                  {sparkValues.length > 0
                    ? `cumulative · ${sparkValues.length} period${sparkValues.length === 1 ? '' : 's'} (latest = total)`
                    : 'first period — no history yet'}
                </div>
              </div>
              <ChartCountdownPanel
                periodNum={periodNum}
                ttnpSeconds={ttnp.data !== undefined ? Number(ttnp.data) : undefined}
              />
            </div>
          </div>
          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line)', display: 'flex', gap: 10 }}>
            <Link to="/auction" className="btn btn-prim btn-block">Open auction →</Link>
            <Link to="/auditing" className="btn btn-block">Open requests</Link>
          </div>
        </div>

        <div className="panel">
          <div className="p-head">
            <h3>The DVBC value loop</h3>
            <span className="src">3 conditions · 2 live</span>
          </div>
          <div className="p-body">
            <ValueLoopMini
              totalEthIn={totalRaisedEth}
              fundedEth={fundedToLabsEth}
              supplyMeth={supplyMeth}
            />
          </div>
        </div>
      </div>

      <div className="kpi-row mb-24">
        <Kpi
          l="METH SUPPLY"
          v={supplyMeth != null ? fmt.meth(supplyMeth) : '—'}
          d={supply.isLoading ? 'loading…' : 'on-chain'}
        />
        <Kpi
          l="VAULT TVL"
          v={vaultEth != null ? <>{vaultEth.toFixed(4)}<span className="u">ETH</span></> : '—'}
          d={vault.isLoading ? 'loading…' : 'crowdsale balance'}
          tone="up"
        />
        <Kpi
          l="CONTRIBUTORS · LIVE"
          v={contributors.data != null ? String(Number(contributors.data)) : '—'}
          d="period in progress"
        />
        <Kpi
          l="EMISSION/PERIOD"
          v={<>{tokensPerPeriod.data !== undefined ? Number(tokensPerPeriod.data).toLocaleString('en-US') : '—'}<span className="u">METH</span></>}
          d="tokensCreatedPerPeriod"
        />
        <Kpi
          l="YOUR BALANCE"
          v={
            account
              ? <>{fmt.meth(balanceMeth)}<span className="u">METH</span></>
              : <span style={{ fontSize: 14, color: 'var(--dim)' }}>connect wallet</span>
          }
          d={
            account
              ? voteWeightPct != null
                ? `vote weight ${voteWeightPct.toFixed(2)}%`
                : 'vote weight —'
              : '—'
          }
        />
        <Kpi
          l="CLAIMABLE METH"
          v={
            account
              ? <>{fmt.meth(claimableMeth)}<span className="u">METH</span></>
              : <span style={{ fontSize: 14, color: 'var(--dim)' }}>—</span>
          }
          d={account ? 'from past periods' : '—'}
          tone={claimableMeth > 0 ? 'up' : undefined}
        />
      </div>

      <div className="row r-2u1 mb-24">
        <div className="panel">
          <div className="p-head">
            <h3>Open requests · top hype</h3>
            <Link to="/auditing" className="src" style={{ color: 'var(--ctx)' }}>View all →</Link>
          </div>
          <div className="tbl">
            <div className="th" style={{ gridTemplateColumns: '60px 1fr 100px 80px 90px' }}>
              <span>ID</span><span>TITLE</span><span>LAB</span><span>HYPE</span><span>STATUS</span>
            </div>
            {topRequests.length === 0 ? (
              <div style={{ padding: 18, color: 'var(--dim)', fontSize: 12, fontFamily: 'var(--mono)' }}>
                {reqN === 0
                  ? <>No requests yet. <Link to="/request/new" className="acc">Submit the first →</Link></>
                  : <>All requests have a verdict cast. <Link to="/auditing" className="acc">See archive →</Link></>}
              </div>
            ) : (
              topRequests.map(({ id, row }) => (
                <Link key={id} to={`/request/${id}`} className="tr"
                      style={{ gridTemplateColumns: '60px 1fr 100px 80px 90px', textDecoration: 'none', fontSize: 12 }}>
                  <span className="pid">#{String(id).padStart(3, '0')}</span>
                  <span className="who">{row.title || <span className="dim">(no title)</span>}
                    <small>{short(row.labWallet)}</small></span>
                  <span>{short(row.labWallet)}</span>
                  <span>↑ {fmt.meth(weiToEth(row.hype))}</span>
                  <span><Pill tone="blue">Voting</Pill></span>
                </Link>
              ))
            )}
          </div>
        </div>

        <div className="panel">
          <div className="p-head">
            <h3>Open ballots</h3>
            <Link to="/governance" className="src" style={{ color: 'var(--ctx)' }}>View all →</Link>
          </div>
          {openProposals.length === 0 ? (
            <div style={{ padding: 18, color: 'var(--dim)', fontSize: 12, fontFamily: 'var(--mono)' }}>
              {propN === 0
                ? <>No proposals yet. <Link to="/governance" className="acc">Submit one →</Link></>
                : 'No open ballots — all proposals have closed or passed.'}
            </div>
          ) : (
            <div className="tbl">
              <div className="th" style={{ gridTemplateColumns: '60px 1fr 110px 90px' }}>
                <span>ID</span><span>STATUS</span><span>QUORUM</span><span>ENDS IN</span>
              </div>
              {openProposals.map(({ id, row }) => {
                const remaining = Math.max(0, Number(row.deadLine) - now);
                const yes = weiToEth(row.voteCount);
                const threshold = weiToEth(row.threshold);
                return (
                  <Link key={id} to="/governance" className="tr"
                        style={{ gridTemplateColumns: '60px 1fr 110px 90px', textDecoration: 'none', fontSize: 12 }}>
                    <span className="pid">#{String(id).padStart(3, '0')}</span>
                    <span><Pill tone="amber">Voting</Pill></span>
                    <span>{fmt.meth(yes)} / {fmt.meth(threshold)}</span>
                    <span className="mono">{fmt.ttlShort(remaining)}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="ph">
        <h1>Deployed contracts <span className="acc">/ chainId 11155111</span></h1>
        <div className="meta"><span>VERIFIED ON ETHERSCAN</span></div>
      </div>
      <ContractsStrip />
    </Page>
  );
}

/* -------------------- helpers -------------------- */

function Kpi({
  l, v, d, tone,
}: { l: string; v: ReactNode; d: ReactNode; tone?: 'up' | 'dn' }) {
  return (
    <div className="kpi">
      <span className="l">{l}</span>
      <span className="v">{v}</span>
      <span className={`d${tone ? ' ' + tone : ''}`}>{d}</span>
    </div>
  );
}

/**
 * Sparkline from cumulative ETH-raised values. Renders a smooth Catmull-Rom
 * spline (converted to cubic-Bézier "C" commands) for a continuous curve
 * even with a handful of points. onMouseMove tracks cursor X, snaps to the
 * nearest data point, and shows a small floating popup with the cumulative
 * value at that point.
 */
function Sparkline({ values, startPeriod }: { values: number[]; startPeriod: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ idx: number; cursorX: number; cursorY: number } | null>(null);

  if (values.length === 0) {
    return (
      <div style={{ width: '100%', height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--dim)' }}>
        — no closed periods yet —
      </div>
    );
  }
  const VB_W = 400;
  const VB_H = 80;
  const max = Math.max(0.0001, ...values);
  const min = Math.min(0, ...values);
  const range = Math.max(0.0001, max - min);
  const N = values.length;
  const points = values.map((v, i) => {
    const x = N === 1 ? VB_W / 2 : (i / (N - 1)) * VB_W;
    const y = (VB_H - 4) - ((v - min) / range) * (VB_H - 10);
    return [x, y] as const;
  });

  // Catmull-Rom → Bezier conversion.
  function smoothPath(pts: readonly (readonly [number, number])[]): string {
    if (pts.length < 2) return `M ${pts[0][0]} ${pts[0][1]}`;
    if (pts.length === 2) return `M ${pts[0][0]} ${pts[0][1]} L ${pts[1][0]} ${pts[1][1]}`;
    let d = `M ${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] ?? pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] ?? pts[i + 1];
      const c1x = p1[0] + (p2[0] - p0[0]) / 6;
      const c1y = p1[1] + (p2[1] - p0[1]) / 6;
      const c2x = p2[0] - (p3[0] - p1[0]) / 6;
      const c2y = p2[1] - (p3[1] - p1[1]) / 6;
      d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
    }
    return d;
  }
  const linePath = smoothPath(points);
  const fillPath = `${linePath} L ${VB_W} ${VB_H} L 0 ${VB_H} Z`;

  function onMove(e: React.MouseEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const relX = cx / rect.width;
    // Snap to the nearest data index.
    const idx = Math.max(0, Math.min(N - 1, Math.round(relX * (N - 1))));
    setHover({ idx, cursorX: cx, cursorY: cy });
  }

  const hoverPt = hover ? points[hover.idx] : null;
  // The N includes the live period as the trailing point (appended by parent);
  // so values[i] for i in 0..N-2 maps to startPeriod + i (closed periods),
  // and values[N-1] is the LIVE current period.
  const labelForIdx = (i: number) => (i === N - 1 ? `P${startPeriod + N - 1}* (live)` : `P${startPeriod + i}`);

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
      style={{ position: 'relative', width: '100%', height: 96, cursor: 'crosshair' }}
    >
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block' }}>
        <defs>
          <linearGradient id="sparkGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#3ddb89" stopOpacity={0.42} />
            <stop offset="100%" stopColor="#3ddb89" stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={fillPath} fill="url(#sparkGrad)" />
        <path d={linePath} fill="none" stroke="#3ddb89" strokeWidth={1.4} strokeLinejoin="round" strokeLinecap="round" />
        {hoverPt && (
          <>
            <line x1={hoverPt[0]} x2={hoverPt[0]} y1={0} y2={VB_H} stroke="#3ddb89" strokeWidth={0.5} strokeDasharray="2 3" opacity={0.7} />
            <circle cx={hoverPt[0]} cy={hoverPt[1]} r={3} fill="#3ddb89" stroke="#0a0f1c" strokeWidth={1.2} />
          </>
        )}
      </svg>
      {hover && (
        <div
          className="panel mono"
          style={{
            position: 'absolute',
            // Offset so the popup doesn't sit under the cursor; clamp to
            // the right edge so it never overflows the container.
            left: Math.min(hover.cursorX + 12, (ref.current?.clientWidth ?? 400) - 170),
            top: Math.max(0, hover.cursorY - 50),
            pointerEvents: 'none',
            padding: '6px 10px',
            fontSize: 11,
            lineHeight: 1.45,
            background: 'var(--panel)',
            border: '1px solid var(--line-2)',
            borderLeft: '2px solid var(--ctx)',
            color: 'var(--ink)',
            whiteSpace: 'nowrap',
            zIndex: 10,
            boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
          }}
        >
          <div style={{ color: 'var(--ctx)', letterSpacing: 1.2, fontSize: 9, textTransform: 'uppercase', marginBottom: 2 }}>
            {labelForIdx(hover.idx)}
          </div>
          <div>cumulative · <b>{values[hover.idx].toFixed(4)} ETH</b></div>
        </div>
      )}
    </div>
  );
}

/**
 * Right-side panel that sits alongside the sparkline: shows the current
 * period number and a live-ticking countdown until rollover.
 */
function ChartCountdownPanel({ periodNum, ttnpSeconds }: { periodNum: number | null; ttnpSeconds: number | undefined }) {
  const cd = useCountdown(ttnpSeconds);
  const amber = cd !== null && cd < 60;
  return (
    <div
      style={{
        flex: '0 0 auto',
        width: 130,
        padding: '8px 10px',
        background: 'var(--panel-2)',
        border: '1px solid var(--line-2)',
        borderLeft: `2px solid ${amber ? 'var(--amber)' : 'var(--ctx)'}`,
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        gap: 4,
      }}
    >
      <span className="mono" style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: 1.4, textTransform: 'uppercase' }}>
        period {periodNum ?? '…'}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: 1,
          fontVariantNumeric: 'tabular-nums',
          color: amber ? 'var(--amber)' : 'var(--ink)',
          lineHeight: 1,
        }}
      >
        {formatCountdown(cd)}
      </span>
      <span className="mono" style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: 1.2, textTransform: 'uppercase' }}>
        until close
      </span>
    </div>
  );
}

function ValueLoopMini({
  totalEthIn, fundedEth, supplyMeth,
}: {
  /** Cumulative ETH contributed across all crowdsale periods (cond. b lifetime). */
  totalEthIn: number | null;
  /** Cumulative ETH disbursed to labs across all claimed milestones. */
  fundedEth: number | null;
  supplyMeth: number | null;
}) {
  return (
    <div className="loop-mini">
      <svg viewBox="0 0 380 280" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <marker id="ar" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={7} markerHeight={7} orient="auto">
            <path d="M0 0 L10 5 L0 10 Z" fill="#3ddb89" />
          </marker>
          <marker id="ar-d" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={7} markerHeight={7} orient="auto">
            <path d="M0 0 L10 5 L0 10 Z" fill="#525c75" />
          </marker>
        </defs>
        <circle cx={190} cy={140} r={120} fill="none" stroke="#1d2435" strokeDasharray="2 4" />
        <path d="M 250 36 Q 320 60 320 140" fill="none" stroke="#3ddb89" strokeWidth={1.5} markerEnd="url(#ar)" />
        <path d="M 320 160 Q 305 240 240 240" fill="none" stroke="#3ddb89" strokeWidth={1.5} markerEnd="url(#ar)" />
        <path d="M 140 240 Q 60 240 60 160" fill="none" stroke="#525c75" strokeWidth={1.5} strokeDasharray="5 4" markerEnd="url(#ar-d)" />
        <path d="M 60 140 Q 60 60 130 36" fill="none" stroke="#3ddb89" strokeWidth={1.5} markerEnd="url(#ar)" />
        <text x={190} y={135} textAnchor="middle" fill="#5a6680" fontFamily="JetBrains Mono" fontSize={9} letterSpacing={1.2}>METH SUPPLY</text>
        <text x={190} y={156} textAnchor="middle" fill="#e8edf5" fontFamily="Inter" fontSize={20} fontWeight={600} letterSpacing={-0.5}>
          {supplyMeth != null ? fmt.meth(supplyMeth) : '—'}
        </text>
        <text x={190} y={173} textAnchor="middle" fill="#5a6680" fontFamily="JetBrains Mono" fontSize={9} letterSpacing={1}>cond. c · live</text>
      </svg>
      <Link
        to="/auction"
        className="loop-node"
        style={{ left: '8%', top: 0 }}
        title="Total ETH contributed across the auction's lifetime (all periods, cumulative)."
      >
        <span className="l">cond. b · TOTAL ETH IN</span>
        <span className="v">{totalEthIn != null ? totalEthIn.toFixed(4) : '—'}<span className="u">ETH</span></span>
      </Link>
      <Link
        to="/auditing"
        className="loop-node"
        style={{ right: 0, top: '35%', textAlign: 'right' }}
        title="Sum of ETH disbursed to labs across every milestone they've claimed (status ≥ Claimed)."
      >
        <span className="l">RESEARCH FUNDED</span>
        <span className="v">{fundedEth != null ? fundedEth.toFixed(4) : '—'}<span className="u">ETH</span></span>
      </Link>
      <Link to="/account" className="loop-node planned" style={{ left: '8%', bottom: 0 }}>
        <span className="l">cond. a · IP outputs</span>
        <span className="v">0<span className="u">licenses</span></span>
      </Link>
    </div>
  );
}

